// ============================================================================
// usePairedView — Personal Finance V2 (Blueprint §8)
// Agrupa filteredPostings em pares (RECEITA+DESPESA) e singletons.
// Pure hook — sem side effects.
// ============================================================================

import { useMemo } from 'react';

/**
 * @param {object[]} filteredPostings — já sorted por date/pairId/sortOrder
 * @returns {Array<{ type: 'pair', pairId: string, income: object, expense: object } | { type: 'single', posting: object }>}
 */
export function usePairedView(filteredPostings) {
    return useMemo(() => {
        const result = [];
        const consumed = new Set();

        // Index by pairId for O(1) lookup
        const byPairId = {};
        filteredPostings.forEach(p => {
            if (p.pairId) {
                if (!byPairId[p.pairId]) byPairId[p.pairId] = [];
                byPairId[p.pairId].push(p);
            }
        });

        for (const p of filteredPostings) {
            if (consumed.has(p.id)) continue;

            if (p.pairId && byPairId[p.pairId] && byPairId[p.pairId].length >= 2) {
                const siblings = byPairId[p.pairId];
                const income = siblings.find(s => s.group === 'RECEITAS');
                const expense = siblings.find(s => s.group !== 'RECEITAS');

                if (income && expense && !consumed.has(income.id) && !consumed.has(expense.id)) {
                    result.push({
                        type: 'pair',
                        pairId: p.pairId,
                        income,
                        expense,
                        status: income.status || expense.status,
                    });
                    consumed.add(income.id);
                    consumed.add(expense.id);
                    continue;
                }
            }

            // Singleton (sem par ou par incompleto)
            result.push({ type: 'single', posting: p });
            consumed.add(p.id);
        }

        return result;
    }, [filteredPostings]);
}
